import type { ExtraFeeBasis } from '../leasing-settings/leasing-settings.interface';
import type { BaseDocument } from '../mongo/base-document.interface';
import type { TerritoryCurrency } from '../territory/territory.interface';
import type { ContractFeeCode } from './contract-fee-codes';
import type { ContractFeeRebill } from './contract-fee-rebill';

/**
 * The contract skeleton shared by the Leasing contract stories (epic SWOOP-338): the wizard that drafts
 * and creates a contract (SWOOP-346), the contracts list with archive and delete (SWOOP-345) and the
 * contract page (SWOOP-365). It fixes the stored shape so those stories can be built in parallel; each
 * story adds its own routes, bodies and rules on top. Later stories (quotations, approvals, magic-link
 * signature, agreements, addenda) extend it rather than reshaping it.
 */

/**
 * A contract's lifecycle (SWOOP-345). The wizard creates `DRAFT` (Save draft) or `READY_TO_SEND`
 * (Create contract) (SWOOP-346); every later status is set by a later story.
 *
 * `ARCHIVED` is a status, NOT a soft delete: an archived contract must stay openable and restorable, so
 * it keeps `deletedAt` null (the vehicle pattern, SWOOP-183) and remembers `statusBeforeArchive`. Only
 * a contract never sent can be deleted, and that delete is the soft delete (`deletedAt`).
 */
export const CONTRACT_STATUS = {
  DRAFT: 'DRAFT',
  READY_TO_SEND: 'READY_TO_SEND',
  SENT_TO_CUSTOMER: 'SENT_TO_CUSTOMER',
  EXPIRED: 'EXPIRED',
  SIGNED: 'SIGNED',
  ACTIVE: 'ACTIVE',
  HOLDOVER: 'HOLDOVER',
  ENDING: 'ENDING',
  CLOSED: 'CLOSED',
  // Set by the termination stories (SWOOP-383); shown and filterable from SWOOP-345. No document holds
  // it yet, so no migration.
  TERMINATED: 'TERMINATED',
  ARCHIVED: 'ARCHIVED',
} as const;

export type ContractStatus = (typeof CONTRACT_STATUS)[keyof typeof CONTRACT_STATUS];

/**
 * Instrument type, set on the Contract terms step (SWOOP-346). The options are fixed in code. Which one
 * is offered follows the customer picked on step 1: `ADDENDUM` only when that customer has a signed,
 * active master (SWOOP-347); otherwise it is fixed to `MASTER`.
 *
 * `ADDENDUM` is a VEHICLE addendum only: a contract row that adds vehicles under a master and is numbered
 * `{master}-A{n}`. Every addendum query (numbering, coverage, schedule, list totals, picker) counts these
 * rows. The other documents the leasing design also calls "addenda" are not contract rows: an extension
 * (SWOOP-415, `-EXT01`) is recorded in its own `contract-extensions` collection, and amendments (SWOOP-380)
 * and substitutions (SWOOP-379) live with their own records. If one of them is ever stored as a contract row under a
 * master, add an addendum-type field first and filter every addendum query on it (decision of 30 Oct 2026).
 */
export const CONTRACT_KIND = { MASTER: 'MASTER', ADDENDUM: 'ADDENDUM' } as const;

export type ContractKind = (typeof CONTRACT_KIND)[keyof typeof CONTRACT_KIND];

/** Lease mode, applying to every line (SWOOP-350). The options are fixed in code. */
export const LEASE_MODE = { OPERATING_LEASE: 'OPERATING_LEASE', LEASE_TO_OWN: 'LEASE_TO_OWN' } as const;

export type LeaseMode = (typeof LEASE_MODE)[keyof typeof LEASE_MODE];

/** How the mileage allowance is counted (SWOOP-357). The options are fixed in code. */
export const MILEAGE_ALLOWANCE_MODE = { PER_VEHICLE: 'PER_VEHICLE', POOLED: 'POOLED' } as const;

export type MileageAllowanceMode = (typeof MILEAGE_ALLOWANCE_MODE)[keyof typeof MILEAGE_ALLOWANCE_MODE];

/**
 * An agreement's status (SWOOP-365, SWOOP-366). One agreement per vehicle: on signature each line turns
 * into `quantity` agreements (SWOOP-364). Agreements are not stored yet — the collection arrives with
 * the signature story — so the contract page's Agreements tab shows its empty state until then.
 */
export const AGREEMENT_STATUS = {
  PENDING: 'PENDING',
  ASSIGNED: 'ASSIGNED',
  ON_LEASE: 'ON_LEASE',
  EARLY_TERMINATED: 'EARLY_TERMINATED',
  SUBSTITUTED: 'SUBSTITUTED',
} as const;

export type AgreementStatus = (typeof AGREEMENT_STATUS)[keyof typeof AGREEMENT_STATUS];

/**
 * Values a new contract starts with on the Contract terms step (SWOOP-346). Anything the territory's
 * Leasing settings do not hold yet is fixed here; payment terms are pre-filled from the territory's
 * default payment terms (SWOOP-354), except the grace period, which is set on the contract only.
 */
export const CONTRACT_DEFAULTS = {
  depositMonths: 1,
  gracePeriodDays: 5,
  kind: CONTRACT_KIND.MASTER,
  leaseMode: LEASE_MODE.OPERATING_LEASE,
  mileageAllowanceMode: MILEAGE_ALLOWANCE_MODE.PER_VEHICLE,
  vatExempt: false,
} as const;

/** Validation bounds shared by the API DTO and the wizard. */
export const CONTRACT_LIMITS = {
  /** Deposit, in months of rent (SWOOP-351). */
  depositMonthsMax: 6,
  /** Decimal places an amount may carry (SWOOP-349). */
  amountDecimals: 2,
  archiveReasonMax: 500,
  exemptionReferenceMax: 100,
  /** A recorded quotation's blob path and file name (SWOOP-394). */
  quotationBlobPathMax: 300,
  quotationFileNameMax: 255,
} as const;

/**
 * The contract's payment terms (SWOOP-354). Invoicing cycle and payment due are the free-text labels the
 * territory offers, copied onto the contract; changing the territory defaults never changes a contract.
 */
export interface ContractPaymentTerms {
  gracePeriodDays: number;
  invoicingCycle: string;
  /** Late-payment interest, percent a year. */
  lateInterestPercent: number;
  paymentDue: string;
}

/**
 * One vehicle line (SWOOP-346, SWOOP-348): one vehicle specification, `quantity` identical vehicles.
 * Lines have no id: a line is its position in `lines` (line 1 is index 0). They are edited freely while
 * the contract is unsent and are frozen once it is sent.
 *
 * Every field except `quantity` is null while the draft is unfinished. Create contract (`READY_TO_SEND`)
 * requires every line to have a vehicle, a `unitPrice`, a term, an annual mileage and its
 * `invoicedMonthlyNet`.
 *
 * `unitPrice` is typed on the line and required to create the contract; `dealerSupplierId` is optional,
 * one of the territory's vehicle-dealer suppliers. Neither is read from the fleet, so a later change in the
 * core does not change the contract. `servicePackageId` null means the built-in "No" package
 * (SWOOP-341).
 */
export interface ContractLine {
  annualMileage: number | null;
  dealerSupplierId: string | null;
  /** Engine, a vendor id kept as a string like `Vehicle.engineId`. */
  engineId: string | null;
  /**
   * Price per km beyond the mileage allowance, typed on the line (meeting, 2 Nov 2026: no longer read from the
   * vehicle). Required to send the contract, 0 allowed; copied onto every agreement the line creates, where a
   * modify can change it (SWOOP-414). Null only while drafting.
   */
  extraKmPrice: number | null;
  insurancePackageId: string | null;
  /** Typed amount invoiced per vehicle per month, excluding VAT (SWOOP-349). */
  invoicedMonthlyNet: number | null;
  makeId: string | null;
  modelId: string | null;
  quantity: number;
  servicePackageId: string | null;
  termMonths: number | null;
  unitPrice: number | null;
}

/**
 * One extra fee or tax on the contract (SWOOP-343, SWOOP-346): a copy of a territory fee taken when the
 * contract is first saved, so a later change to the territory's list never changes the contract. Rows have
 * no id (territory fee names may repeat): a fee is its position in `extraFees`.
 *
 * `rate` is the territory fee's amount, read with `basis` (per mille, percent, or a fixed amount). A
 * mandatory fee is always `applied`; an optional one is applied when ticked on Review. `amount` is worked
 * out by the server on every save, to the cent, and is 0 when the fee is not applied. `code` is the territory
 * fee's code, copied with it (SWOOP-416): rules act on the code, never on the name.
 */
export interface ContractExtraFee {
  amount: number;
  applied: boolean;
  basis: ExtraFeeBasis;
  code: ContractFeeCode;
  mandatory: boolean;
  name: string;
  rate: number;
}

/** The monthly figures of a contract, computed by {@link computeContractTotals}. */
export interface ContractTotals {
  monthlyGross: number;
  monthlyNet: number;
  monthlyVat: number;
  vehicleCount: number;
}

/**
 * The outcome of the latest attempt to generate a contract's document from its territory's in-use template
 * (SWOOP-395): `GENERATED` (a PDF exists), `NO_TEMPLATE` (the territory had none in use) or `FAILED` (the
 * template could not be rendered; the reason is `failureCode`).
 */
export const CONTRACT_DOCUMENT_STATUS = {
  FAILED: 'FAILED',
  GENERATED: 'GENERATED',
  NO_TEMPLATE: 'NO_TEMPLATE',
} as const;

export type ContractDocumentStatus = (typeof CONTRACT_DOCUMENT_STATUS)[keyof typeof CONTRACT_DOCUMENT_STATUS];

/** How a contract was signed (SWOOP-361): the customer accepted the magic link, or a signed PDF was uploaded. */
export const CONTRACT_SIGNED_VIA = { MAGIC_LINK: 'MAGIC_LINK', MANUAL: 'MANUAL' } as const;

export type ContractSignedVia = (typeof CONTRACT_SIGNED_VIA)[keyof typeof CONTRACT_SIGNED_VIA];

/**
 * The contract's link to its current generated document (SWOOP-395). Null on the contract until the first
 * generation attempt; written only while the contract is Ready to send and never sent, then frozen. Earlier
 * generated documents stay in `documents`, owned by the contract: that is the history.
 *
 * `TDate` is `Date` as stored and `string` (ISO) on the API shape, like the contract's own dates.
 */
export interface ContractDocumentLink<TDate = Date> {
  /** When the latest attempt STARTED reading the contract (display only; the generation fence is `documentGeneration`). */
  attemptedAt: TDate;
  /** The current generated PDF (FK → documents) when `GENERATED`; null otherwise. */
  documentId: string | null;
  /** The `HetivoError` code of a `FAILED` attempt (e.g. `BadRequest.DocumentTemplateCorrupt`); null otherwise. */
  failureCode: string | null;
  status: ContractDocumentStatus;
}

/**
 * Who the contract went to and how it was signed (SWOOP-361). Null on the contract until it is sent or signed
 * by hand. It stores no request state: deliveries, expiry, first open, outcome and comment are read from the
 * magic link, and so is the recipient email (the newest request's).
 *
 * `TDate` is `Date` as stored and `string` (ISO) on the API shape, like the contract's own dates.
 */
export interface ContractSignature<TDate = Date> {
  /** The magic-link request that was accepted (FK → magic-links); null unless signed by link. */
  magicLinkId: string | null;
  /** The caller who sent the contract (FK → users); null when signed by hand without a send. */
  sentByUserId: string | null;
  /** The customer's signatory, read from the customer at send (contact person, else its name). */
  signatoryName: string | null;
  signedAt: TDate | null;
  /** The user who uploaded the hand-signed copy (FK → users); null unless signed by hand. */
  signedByUserId: string | null;
  /** The `CONTRACT_SIGNED_COPY` document (FK → documents); null until a signed copy exists. */
  signedCopyDocumentId: string | null;
  signedVia: ContractSignedVia | null;
}

/**
 * A leasing contract: one document per contract in `contracts`, tenant- and territory-scoped.
 *
 * - `number` is given when the contract is first saved, draft included: `MA-YY-NNNN` (e.g. MA-26-0012),
 *   unique per tenant and never reused. An addendum is numbered from its master (MA-26-0012-A1, SWOOP-347).
 * - `version` starts at 1. One document holds the current version; a sent version that changes opens the
 *   next one (later stories). Lists show one row per contract, not per version.
 * - `vehicleCount` and the monthly amounts are stored copies of {@link computeContractTotals}, recomputed
 *   by the server on every write, so the list can sort and page on them.
 * - `vatRatePercent` is copied from the territory (`Territory.vatRate`) when the contract is priced; a
 *   later change of the territory rate does not change it.
 */
export interface Contract extends BaseDocument, ContractTotals {
  /** The Account Manager owning the contract (a user id). */
  accountManagerId: string;
  archiveReason: string | null;
  archivedAt: Date | null;
  customerId: string;
  /** Months of rent held as deposit, 0 to 6 (SWOOP-351). */
  depositMonths: number;
  /**
   * The master contract this one was duplicated from (SWOOP-397); null unless it was created through
   * Duplicate. Provenance only — nothing is copied from the source server-side.
   */
  duplicatedFromContractId: string | null;
  /**
   * The `number` of the source contract (SWOOP-397), copied once at create so the change log can narrate
   * "Duplicated from <number>" without resolving the id; null unless duplicated. Numbers are never reused.
   */
  duplicatedFromNumber: string | null;
  /** Start date plus the longest term on the lines; null until both are known. */
  endDate: Date | null;
  /** Extra fees and taxes copied from the territory on the first save (SWOOP-346). */
  extraFees: ContractExtraFee[];
  /**
   * Fees re-billed to the customer (SWOOP-416): several requests per fee code, while their amounts add up to at most
   * the amount due (product decision 2026-11-03); empty until the first is generated.
   */
  feeRebills: ContractFeeRebill[];
  /** The contract's generated document (SWOOP-395); null until the first generation attempt. */
  document: ContractDocumentLink | null;
  /**
   * Monotonic generation-claim counter (SWOOP-395, PR #305 review). Each `generateDocument` run atomically
   * `$inc`s it BEFORE reading the contract and uses the new value as its token; the document write lands only
   * while the stored counter still equals that token, so overlapping generations are fenced newest-claim-wins
   * rather than finish-order-wins. Server-managed; absent on an older row reads as 0 (`$inc` creates it).
   */
  documentGeneration?: number;
  /**
   * Revision of the inputs of a fee's amount due (SWOOP-416, PR #378 review): `lines`, `extraFees`, `vatExempt` and
   * `vatRatePercent`. Every write that changes one of them `$inc`s it in the same update, and a fee re-bill's guarded
   * push matches only while the stored value is still the one the amount due was worked out from, so a re-bill is
   * never recorded against a due an amendment has since lowered. A re-bill itself does not change a due input, so it
   * leaves the revision alone. Server-managed; absent on an older row reads as 0, so no migration is needed.
   */
  dueInputsRevision?: number;
  /** Required when `vatExempt` (SWOOP-352); null otherwise. */
  exemptionReference: string | null;
  kind: ContractKind;
  leaseMode: LeaseMode;
  lines: ContractLine[];
  /** The master of an addendum (SWOOP-347); null on a master. */
  masterContractId: string | null;
  mileageAllowanceMode: MileageAllowanceMode;
  number: string;
  paymentTerms: ContractPaymentTerms;
  /** When the contract was first sent to the customer (SWOOP-361). Null means never sent: it can still be deleted. */
  sentAt: Date | null;
  /** Who the contract went to and how it was signed (SWOOP-361); null until sent or signed by hand. */
  signature: ContractSignature | null;
  startDate: Date | null;
  status: ContractStatus;
  /** The status an archived contract is restored to (SWOOP-345); null unless `ARCHIVED`. */
  statusBeforeArchive: ContractStatus | null;
  territoryId: string;
  /**
   * VAT certificate write-fence counter (SWOOP-352, PR #327 review). A certificate upload or delete `$inc`s it
   * inside its transaction, while the contract is live, not archived and `vatExempt`, so a racing archive or
   * VAT-exempt untick (a write on the same row) conflicts with it. Written with `timestamps: false`, so it never
   * moves `updatedAt`, the wizard's optimistic-concurrency token. Server-managed; absent on an older row reads as 0.
   */
  vatCertificateFence?: number;
  vatExempt: boolean;
  vatRatePercent: number;
  version: number;
}

/**
 * One vehicle specification a contract line can pick (SWOOP-346): a make and model of the vehicle master
 * list, with one of its engines. A model's engines are its master-list engines plus any other engine found
 * on the territory's vehicles of that model; a model with none offers one option with a `null` engine. Every
 * specification can be picked, whether or not the fleet holds such a vehicle, so a contract can order a car
 * the fleet does not have yet. Read model only, never stored.
 */
export interface ContractVehicleOption {
  engineId: string | null;
  makeId: string;
  makeName: string;
  modelId: string;
  modelName: string;
}

/** A make a contract line can pick (SWOOP-346): every make of the vehicle master list, models or not. */
export interface ContractMakeOption {
  id: string;
  name: string;
}

/**
 * A dealer a contract line can pick (SWOOP-346): an ACTIVE vehicle-dealer supplier of the contract's
 * territory, served through the contract permission (an Account Manager holds no `suppliers:list`).
 */
export interface ContractDealerOption {
  id: string;
  name: string;
}

export type CreateContract = Omit<Contract, 'createdAt' | 'deletedAt' | 'id' | 'tenantId' | 'updatedAt'>;

/**
 * One row of the contracts list (SWOOP-345): a master contract's list columns plus its addenda tally.
 * `statusBeforeArchive` and `sentAt` are carried so the row menu applies the same action rules as the
 * API (see `contract-actions.ts`). `addendaInProgress` counts addenda whose status is not one of
 * `CONTRACT_ADDENDUM_SETTLED_STATUSES`; both counts are zero until addenda exist (SWOOP-347).
 * `currency` is the contract's territory currency: a tenant-level list mixes territories, and each
 * row's monthly amount is shown in its own territory's currency, never converted.
 */
export interface ContractListItem {
  addendaCount: number;
  addendaInProgress: number;
  currency: TerritoryCurrency;
  customerId: string;
  customerName: string;
  id: string;
  kind: ContractKind;
  monthlyGross: number;
  number: string;
  sentAt: string | null;
  status: ContractStatus;
  statusBeforeArchive: ContractStatus | null;
  updatedAt: string;
  vehicleCount: number;
  version: number;
}

/**
 * The contracts-list search (SWOOP-345). `territoryId` is the territory selected in the master selector
 * (a selected location sends its territory) and must be in the caller's scope; absent — the tenant is
 * selected — the list covers every territory the caller is staffed in, like the other core lists.
 * `search` matches the contract number or the customer's name; `status` absent means every status
 * except `ARCHIVED`.
 */
export interface SearchContractCriteria {
  search?: string;
  status?: ContractStatus;
  territoryId?: string;
}

/** The API/read shape of a contract: dates as ISO strings, tenancy fields not exposed. */
export interface ContractView extends Omit<
  Contract,
  | 'archivedAt'
  | 'createdAt'
  | 'deletedAt'
  | 'document'
  | 'documentGeneration'
  | 'dueInputsRevision'
  | 'endDate'
  | 'feeRebills'
  | 'sentAt'
  | 'signature'
  | 'startDate'
  | 'tenantId'
  | 'updatedAt'
  | 'vatCertificateFence'
> {
  archivedAt: string | null;
  createdAt: string;
  document: ContractDocumentLink<string> | null;
  endDate: string | null;
  feeRebills: ContractFeeRebill<string>[];
  sentAt: string | null;
  signature: ContractSignature<string> | null;
  startDate: string | null;
  updatedAt: string;
}

/**
 * One vehicle line as the contract page shows it (SWOOP-365): the stored line plus the names of what it
 * points at, resolved by the server. A name is null while the line has no such id yet (an unfinished
 * draft); `servicePackageName` is the built-in "No" package's label when `servicePackageId` is null.
 */
export interface ContractDetailsLine extends ContractLine {
  insurancePackageName: string | null;
  makeName: string | null;
  modelName: string | null;
  servicePackageName: string;
}

/**
 * The contract page's read shape (SWOOP-365, `GET /contracts/:id/details`): the contract plus everything the
 * page names instead of showing an id — the customer, the Account Manager, each line's vehicle and packages —
 * and the territory's currency for its amounts. `GET /contracts/:id` keeps the bare {@link ContractView} the
 * wizard's edit page reads.
 */
export interface ContractDetails extends Omit<ContractView, 'lines'> {
  accountManagerName: string | null;
  currency: TerritoryCurrency;
  customerName: string;
  lines: ContractDetailsLine[];
}
